import { DEFAULT_LOCALE } from "@granular/fabric3-definitions";
import { GKIllustration, GKIllustrationName } from "@granular/gds";
import { PropsWithChildren } from "react";
import { FormattedMessage, IntlProvider, useIntl } from "react-intl";
import translations from "../../i18n";
import { useUserLocale } from "../hooks/locale/useUserLocale";
import { useOperatingContext } from "../hooks/operatingContext";

type NoContextGuardProps = PropsWithChildren<{
  /**
   * When true, renders a compact inline version suitable for embedding inside
   * a page (e.g. profile). When false (default), renders as a full-page message.
   */
  compact?: boolean;
}>;

/**
 * This component only mounts its children if there is at least one context.
 * Most frontend apps require a context to function properly.
 */
const NoContextGuard: React.FC<NoContextGuardProps> = ({
  children,
  compact = false,
}) => {
  const useOperatingContextResult = useOperatingContext();

  if (useOperatingContextResult.allContexts?.length > 0) {
    return children;
  }

  return <NoContextMessage compact={compact} />;
};

export const NoContextMessage: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  const locale = useUserLocale();
  return (
    <IntlProvider
      locale={locale}
      messages={translations[locale]}
      defaultLocale={DEFAULT_LOCALE}
    >
      <NoContextMessageContent compact={compact} />
    </IntlProvider>
  );
};

const NoContextMessageContent: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  const intl = useIntl();
  const helpLink = intl.formatMessage({ id: "no_context_guard_help_link" });

  return (
    <div
      data-qa="no-context-guard"
      style={{
        width: "100%",
        textAlign: "center",
        padding: compact ? "1.5rem 1rem" : "7rem 1rem",
      }}
    >
      <GKIllustration
        name={GKIllustrationName.FieldLayers}
        style={{
          display: "inline",
          width: compact ? "64px" : "96px",
          height: compact ? "64px" : "96px",
          margin: compact ? "1rem" : "2rem",
        }}
      />
      <h4>
        <FormattedMessage id="no_context_guard_header" />
      </h4>
      <p>
        <FormattedMessage
          id="no_context_guard_text"
          values={{
            newline: <br />,
          }}
        />
      </p>
      <p className="text-muted">
        <FormattedMessage
          id="no_context_guard_help_link_text"
          values={{
            // This substitutes the `<link>` placeholders into actual `<a>` tags.
            link: (chunks) => <a href={helpLink}>{chunks}</a>,
          }}
        />
      </p>
    </div>
  );
};

export default NoContextGuard;
